import React, { useState, useMemo, useEffect } from 'react';
import ReagentStock from './ReagentStock';
import { Tabs, Tab, Button, Spinner, Modal, Form, Alert } from 'react-bootstrap';
import { HomeOutlined } from '@ant-design/icons';
import {
  ActivityLog,
  ClaimHistoryRecord,
  getProcurementApplications,
  getProcurementStatusMap,
  getReagentClaimHistory,
  getReagentRestockHistory,
  getReagents,
  getRecentActivities,
  RestockHistoryRecord,
  submitReagentClaim
} from '../services/api';
import { Reagent } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useStockManagerAccess } from '../hooks/useStockManagerAccess';
import ReagentProcurement from '../components/ReagentProcurement';
import { ApprovalApplicationExtended } from '../types/approval';
import { LimsNavIcon } from '../components/LimsNavIcon';
import { resolveUploadAssetUrl } from '../utils/resolveUploadAssetUrl';
import { DEFAULT_REAGENT_IMAGE } from '../constants/defaultImages';
import {
  getDemoReagents,
  getDemoReagentProcurement,
  getDemoReagentClaimLogs
} from '../demo/demoBrowseMocks';
import './MemberManagement.css';
import '../styles/resourceManagement.css';
import '../styles/clinicalDetailModal.css';

const formatDisplayDateTime = (raw?: string | null) => {
  if (!raw || !String(raw).trim()) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString('zh-CN');
};

const formatDisplayDate = (raw?: string) => {
  if (!raw || !String(raw).trim()) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleDateString('zh-CN');
};

const dash = (v?: string | number | null) => {
  if (v === undefined || v === null) return '—';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '—';
  const s = v.trim();
  return s ? s : '—';
};

const reagentImageSrc = (url?: string) =>
  resolveUploadAssetUrl(url) || url || '';

const stockStatusPill = (qty: number, threshold?: number | null) => {
  const th =
    threshold != null && Number.isFinite(Number(threshold)) && Number(threshold) > 0
      ? Number(threshold)
      : null;
  if (th === null) {
    return (
      <span className="ins-detail-modal__pill ins-detail-modal__pill--avail">
        <span className="ins-detail-modal__pill-dot" aria-hidden />
        未设阈值
      </span>
    );
  }
  const low = (Number(qty) || 0) <= th;
  if (low) {
    return (
      <span className="ins-detail-modal__pill ins-detail-modal__pill--muted">
        <LimsNavIcon name="warning" sizePx={14} style={{ fontVariationSettings: "'FILL' 1" }} />
        低库存
      </span>
    );
  }
  return (
    <span className="ins-detail-modal__pill ins-detail-modal__pill--avail">
      <span className="ins-detail-modal__pill-dot" aria-hidden />
      库存正常
    </span>
  );
};

const ReagentShowcase: React.FC<{ data: Reagent[]; loading: boolean; onRefresh: () => void; onClaimSubmitted: () => void }> = ({
  data,
  loading,
  onRefresh,
  onClaimSubmitted
}) => {
  const [showDetail, setShowDetail] = useState(false);
  const [detailItem, setDetailItem] = useState<Reagent | null>(null);
  const [restockRecords, setRestockRecords] = useState<RestockHistoryRecord[]>([]);
  const [latestApprovedUnitPrice, setLatestApprovedUnitPrice] = useState<number | null>(null);
  const [restockLoading, setRestockLoading] = useState(false);
  const [claimRecords, setClaimRecords] = useState<ClaimHistoryRecord[]>([]);
  const [claimHistoryLoading, setClaimHistoryLoading] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [applyItem, setApplyItem] = useState<Reagent | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [applyMsg, setApplyMsg] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [applyForm, setApplyForm] = useState({
    amount: '1',
    reason: '',
    priority: '普通' as '普通' | '紧急' | '特急'
  });
  const [categoryFilter, setCategoryFilter] = useState('全部');
  const categories = useMemo(
    () => Array.from(new Set(data.map((item) => item.category?.trim() || '未分类'))),
    [data]
  );
  const filteredData = useMemo(
    () =>
      categoryFilter === '全部'
        ? data
        : data.filter((item) => (item.category?.trim() || '未分类') === categoryFilter),
    [data, categoryFilter]
  );

  const openDetail = (item: Reagent) => {
    setDetailItem(item);
    setShowDetail(true);
    setRestockRecords([]);
    setLatestApprovedUnitPrice(null);
    setClaimRecords([]);
    const id = item._id ? String(item._id) : '';
    if (!id) return;
    setRestockLoading(true);
    setClaimHistoryLoading(true);
    void getReagentRestockHistory(id)
      .then((res) => {
        if (res.success && res.data) {
          setRestockRecords(res.data.records || []);
          setLatestApprovedUnitPrice(
            res.data.latestApprovedUnitPrice != null
              ? Number(res.data.latestApprovedUnitPrice)
              : null
          );
        }
      })
      .finally(() => setRestockLoading(false));
    void getReagentClaimHistory(id)
      .then((res) => {
        if (res.success && res.data) {
          setClaimRecords(res.data.records || []);
        }
      })
      .finally(() => setClaimHistoryLoading(false));
  };

  const openApply = (item: Reagent) => {
    setApplyItem(item);
    setApplyForm({ amount: '1', reason: '', priority: '普通' });
    setApplyMsg(null);
    setShowApply(true);
  };

  const [mobilePortrait, setMobilePortrait] = useState(false);
  useEffect(() => {
    const upd = () => setMobilePortrait(window.innerWidth <= 768 && window.innerHeight > window.innerWidth);
    upd();
    window.addEventListener('resize', upd);
    return () => window.removeEventListener('resize', upd);
  }, []);

  const handleSubmitApply = async () => {
    if (!applyItem) return;
    if (!applyForm.reason.trim()) {
      setApplyMsg({ type: 'danger', text: '请填写申领原因' });
      return;
    }
    const amountNum = Number(applyForm.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setApplyMsg({ type: 'danger', text: '申领数量需大于 0' });
      return;
    }
    setSubmitting(true);
    setApplyMsg(null);
    try {
      const response = await submitReagentClaim(applyItem._id || applyItem.key || '', {
        amount: amountNum,
        reason: applyForm.reason.trim(),
        priority: applyForm.priority
      });
      if (!response.success) {
        setApplyMsg({ type: 'danger', text: response.error || '提交失败，请稍后重试' });
        return;
      }
      onClaimSubmitted();
      setApplyMsg({ type: 'success', text: '申领成功，已写入申领记录' });
      setTimeout(() => setShowApply(false), 900);
    } catch {
      setApplyMsg({ type: 'danger', text: '网络异常，请稍后重试' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mm-table-toolbar mb-2">
        <div className="mm-filter-row">
          <span className="mm-filter-label">试剂分类：</span>
          <div className="mm-filter-pills">
            <button
              type="button"
              className={`mm-pill ${categoryFilter === '全部' ? 'is-active' : ''}`}
              onClick={() => setCategoryFilter('全部')}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                className={`mm-pill ${categoryFilter === c ? 'is-active' : ''}`}
                onClick={() => setCategoryFilter(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="rm-showcase-grid">
        {loading ? (
          <div className="rm-empty-panel">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3">加载中...</div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="rm-empty-panel">
            <div>暂无试剂数据</div>
            <Button className="mm-btn-primary mt-3" onClick={onRefresh}>
              刷新
            </Button>
          </div>
        ) : (
          filteredData.map((item: Reagent, idx: number) => (
            <div key={item._id || item.key || idx} className="rm-card">
              <div className="rm-card-media">
                <img
                  src={reagentImageSrc(item.image) || DEFAULT_REAGENT_IMAGE}
                  alt={item.name || item.cas}
                  loading="lazy"
                />
              </div>
              <div className="rm-card-body">
                <h3 className="rm-card-title">{item.name || item.cas}</h3>
                <div className="rm-card-meta">
                  <span>CAS：{item.cas}</span>
                  <br />
                  <span>
                    规格：{item.model || item.spec} · 库存 {item.quantity} {item.unit}
                  </span>
                  <br />
                  <span>单价：{item.unitPrice != null ? `¥${item.unitPrice}` : '—'} · 购买人：{dash(item.purchaser)}</span>
                </div>
                <div className="rm-card-actions">
                  <Button variant="outline-secondary" size="sm" onClick={() => openDetail(item)}>
                    更多详情
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => openApply(item)}>
                    申领
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        centered
        fullscreen={mobilePortrait ? true : undefined}
        dialogClassName="ins-detail-modal__dialog"
        contentClassName="ins-detail-modal__content"
        backdropClassName="ins-detail-modal__backdrop"
        style={mobilePortrait ? { margin: 0 } : undefined}
      >
        {detailItem && (
          <Modal.Body className="ins-detail-modal__body">
            <header className="ins-detail-modal__header">
              <div className="ins-detail-modal__header-left">
                <div className="ins-detail-modal__header-icon" aria-hidden>
                  <LimsNavIcon name="science" sizePx={26} />
                </div>
                <div>
                  <h2 className="ins-detail-modal__title">试剂详情</h2>
                  <div className="ins-detail-modal__subtitle-row">
                    <span className="ins-detail-modal__subtitle-dot" aria-hidden />
                    <span className="ins-detail-modal__subtitle">
                      条目编号：{detailItem._id ? String(detailItem._id) : detailItem.key ? String(detailItem.key) : '—'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="ins-detail-modal__header-right">
                <div className="ins-detail-modal__status-wrap">
                  {stockStatusPill(Number(detailItem.quantity) || 0, detailItem.restockThreshold)}
                </div>
                <button type="button" className="ins-detail-modal__close" onClick={() => setShowDetail(false)} aria-label="关闭">
                  <LimsNavIcon name="close" sizePx={22} />
                </button>
              </div>
            </header>

            <div className="ins-detail-modal__scroll">
              <div className="ins-detail-modal__hero">
                <div className="ins-detail-modal__hero-img-wrap">
                  <img
                    src={reagentImageSrc(detailItem.image) || DEFAULT_REAGENT_IMAGE}
                    alt=""
                    className="ins-detail-modal__hero-img"
                    loading="lazy"
                  />
                </div>
                <div className="ins-detail-modal__hero-copy">
                  <h3 className="ins-detail-modal__section-title">概览</h3>
                  <p className="ins-detail-modal__desc">
                    {[detailItem.name && `名称：${detailItem.name}`, detailItem.cas && `CAS：${detailItem.cas}`]
                      .filter(Boolean)
                      .join(' · ') || '暂无名称与 CAS 信息'}
                  </p>
                  <div className="ins-detail-modal__usage-block">
                    <h3 className="ins-detail-modal__usage-kicker">厂家与规格</h3>
                    <p className="ins-detail-modal__muted">
                      {detailItem.vendor?.trim()
                        ? `厂家：${detailItem.vendor.trim()}`
                        : '厂家：—'}
                      <br />
                      规格：{dash(detailItem.spec || detailItem.model)}
                    </p>
                  </div>
                </div>
              </div>

              <section className="ins-detail-modal__section">
                <div className="ins-detail-modal__section-head">
                  <LimsNavIcon name="info" sizePx={20} className="ins-detail-modal__section-icon" />
                  <h3 className="ins-detail-modal__section-title-lg">基本信息</h3>
                </div>
                <div className="ins-detail-modal__bento">
                  {[
                    { label: '试剂名称', value: dash(detailItem.name || detailItem.cas) },
                    { label: 'CAS', value: dash(detailItem.cas) },
                    { label: '厂家', value: dash(detailItem.vendor) },
                    { label: '规格', value: dash(detailItem.spec || detailItem.model) },
                    { label: '单位', value: dash(detailItem.unit) },
                    {
                      label: '最近审批单价',
                      value:
                        latestApprovedUnitPrice != null
                          ? `¥${Number(latestApprovedUnitPrice).toFixed(2)}`
                          : detailItem.unitPrice != null
                            ? `¥${Number(detailItem.unitPrice).toFixed(2)}`
                            : '—'
                    },
                    {
                      label: '当前库存',
                      value: `${detailItem.quantity ?? 0}${detailItem.unit ? ` ${detailItem.unit}` : ''}`
                    },
                    { label: '存放位置', value: dash(detailItem.location) }
                  ].map((cell) => (
                    <div key={cell.label} className="ins-detail-modal__bento-cell">
                      <span className="ins-detail-modal__field-label">{cell.label}</span>
                      <p className="ins-detail-modal__field-value">{cell.value}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="ins-detail-modal__section">
                <div className="ins-detail-modal__section-head">
                  <LimsNavIcon name="history" sizePx={20} className="ins-detail-modal__section-icon" />
                  <h3 className="ins-detail-modal__section-title-lg">补货记录</h3>
                </div>
                {restockLoading ? (
                  <div className="text-muted small py-2">加载补货记录…</div>
                ) : restockRecords.length === 0 ? (
                  <p className="ins-detail-modal__muted mb-0">暂无已通过审批的补货记录</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>审批通过时间</th>
                          <th>单价</th>
                          <th>数量</th>
                          <th>总价</th>
                          <th>申请人</th>
                        </tr>
                      </thead>
                      <tbody>
                        {restockRecords.map((row) => (
                          <tr key={`${row.applicationId}-${row.projectIndex}`}>
                            <td>{formatDisplayDate(row.approvedAt || undefined)}</td>
                            <td>
                              {row.unitPrice != null ? `¥${Number(row.unitPrice).toFixed(2)}` : '—'}
                            </td>
                            <td>
                              {row.quantity != null
                                ? `${row.quantity}${row.unit ? ` ${row.unit}` : ''}`
                                : '—'}
                            </td>
                            <td>
                              {row.total != null ? `¥${Number(row.total).toFixed(2)}` : '—'}
                            </td>
                            <td>{dash(row.applicant)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="ins-detail-modal__section">
                <div className="ins-detail-modal__section-head">
                  <LimsNavIcon name="assignment_ind" sizePx={20} className="ins-detail-modal__section-icon" />
                  <h3 className="ins-detail-modal__section-title-lg">申领记录</h3>
                </div>
                {claimHistoryLoading ? (
                  <div className="text-muted small py-2">加载申领记录…</div>
                ) : claimRecords.length === 0 ? (
                  <p className="ins-detail-modal__muted mb-0">暂无该试剂的申领记录</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>时间</th>
                          <th>申领人</th>
                          <th>数量</th>
                          <th>原因</th>
                          <th>优先级</th>
                        </tr>
                      </thead>
                      <tbody>
                        {claimRecords.map((row) => (
                          <tr key={row.id}>
                            <td>{formatDisplayDateTime(row.claimedAt)}</td>
                            <td>{dash(row.claimant)}</td>
                            <td>
                              {row.quantity != null
                                ? `${row.quantity}${row.unit ? ` ${row.unit}` : ''}`
                                : '—'}
                            </td>
                            <td>{row.reason?.trim() ? row.reason.trim() : '—'}</td>
                            <td>{row.priority?.trim() ? row.priority.trim() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <div className="ins-detail-modal__split">
                <section className="ins-detail-modal__panel">
                  <h3 className="ins-detail-modal__section-title-lg">采购与签收</h3>
                  <div className="ins-detail-modal__panel-card">
                    <div className="ins-detail-modal__panel-row">
                      <div className="ins-detail-modal__panel-row-left">
                        <LimsNavIcon name="calendar_today" sizePx={20} className="ins-detail-modal__panel-icon" />
                        <span>购买日期</span>
                      </div>
                      <span className="ins-detail-modal__panel-value">{formatDisplayDate(detailItem.buyDate)}</span>
                    </div>
                    <div className="ins-detail-modal__panel-divider" />
                    <div className="ins-detail-modal__panel-row">
                      <div className="ins-detail-modal__panel-row-left">
                        <LimsNavIcon name="person" sizePx={20} className="ins-detail-modal__panel-icon" />
                        <span>签收人</span>
                      </div>
                      <span className="ins-detail-modal__panel-value">{dash(detailItem.receiver)}</span>
                    </div>
                    {detailItem.purchaser?.trim() ? (
                      <>
                        <div className="ins-detail-modal__panel-divider" />
                        <div className="ins-detail-modal__panel-row">
                          <div className="ins-detail-modal__panel-row-left">
                            <LimsNavIcon name="shopping_cart" sizePx={20} className="ins-detail-modal__panel-icon" />
                            <span>采购人</span>
                          </div>
                          <span className="ins-detail-modal__panel-value">{detailItem.purchaser.trim()}</span>
                        </div>
                      </>
                    ) : null}
                  </div>
                </section>
                <section className="ins-detail-modal__panel">
                  <h3 className="ins-detail-modal__section-title-lg">申领说明</h3>
                  <div className="ins-detail-modal__panel-card">
                    <p className="ins-detail-modal__booking-meta" style={{ marginTop: 0 }}>
                      成员可通过「立即申领」提交数量与原因；库存紧张时建议同步走采购或审批流程。安全库存线可在库存管理中配置。
                    </p>
                  </div>
                </section>
              </div>
            </div>

            <footer className="ins-detail-modal__footer">
              <button type="button" className="ins-detail-modal__btn ins-detail-modal__btn--secondary" onClick={() => setShowDetail(false)}>
                关闭
              </button>
              <button
                type="button"
                className="ins-detail-modal__btn ins-detail-modal__btn--primary"
                onClick={() => {
                  const it = detailItem;
                  setShowDetail(false);
                  if (it) openApply(it);
                }}
              >
                <LimsNavIcon name="inventory_2" sizePx={20} />
                立即申领
              </button>
            </footer>
          </Modal.Body>
        )}
      </Modal>

      <Modal show={showApply} onHide={() => setShowApply(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>试剂申领</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {applyMsg && <Alert variant={applyMsg.type}>{applyMsg.text}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>试剂</Form.Label>
              <Form.Control value={applyItem?.name || applyItem?.cas || ''} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>申领数量</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={applyForm.amount}
                onChange={(e) => setApplyForm((prev) => ({ ...prev, amount: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>优先级</Form.Label>
              <Form.Select
                value={applyForm.priority}
                onChange={(e) => setApplyForm((prev) => ({ ...prev, priority: e.target.value as '普通' | '紧急' | '特急' }))}
              >
                <option value="普通">普通</option>
                <option value="紧急">紧急</option>
                <option value="特急">特急</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>申领原因</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={applyForm.reason}
                onChange={(e) => setApplyForm((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="请填写申领原因"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApply(false)}>取消</Button>
          <Button variant="primary" onClick={handleSubmitApply} disabled={submitting}>
            {submitting ? '提交中...' : '确认申领'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ReagentManagement: React.FC = () => {
  const { isDemoBrowseOnly } = useAuth();
  const { canManage: stockCanManage } = useStockManagerAccess('reagent');
  const canManageReagents = isDemoBrowseOnly || stockCanManage;
  const [key, setKey] = useState('showcase');
  const [data, setData] = useState<Reagent[]>([]);
  const [loading, setLoading] = useState(false);
  const [procurementApplications, setProcurementApplications] = useState<ApprovalApplicationExtended[]>([]);
  const [procurementStatusMap, setProcurementStatusMap] = useState<Record<string, string>>({});
  const [claimLogs, setClaimLogs] = useState<ActivityLog[]>([]);
  const [claimLoading, setClaimLoading] = useState(false);

  const fetchData = async () => {
    if (isDemoBrowseOnly) {
      setData(getDemoReagents());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await getReagents();
      if (Array.isArray(res)) {
        setData(res);
      } else if (res.success && Array.isArray(res.data)) {
        setData(res.data);
      } else {
        setData([]);
      }
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcurementApplications = async () => {
    if (isDemoBrowseOnly) {
      setProcurementApplications(getDemoReagentProcurement().applications);
      return;
    }
    try {
      const response = await getProcurementApplications('试剂申请');
      if (response.success && Array.isArray(response.data)) {
        setProcurementApplications(response.data as ApprovalApplicationExtended[]);
      } else {
        setProcurementApplications([]);
      }
    } catch {
      setProcurementApplications([]);
    }
  };

  const fetchProcurementStatus = async () => {
    if (isDemoBrowseOnly) {
      setProcurementStatusMap(getDemoReagentProcurement().statusMap);
      return;
    }
    try {
      const response = await getProcurementStatusMap('试剂申请');
      if (response.success && response.data) {
        const statusMap = Object.keys(response.data).reduce((acc, applicationId) => {
          acc[applicationId] = response.data![applicationId]?.status || '待采购';
          return acc;
        }, {} as Record<string, string>);
        setProcurementStatusMap(statusMap);
      } else {
        setProcurementStatusMap({});
      }
    } catch {
      setProcurementStatusMap({});
    }
  };

  const fetchClaimLogs = async () => {
    if (isDemoBrowseOnly) {
      setClaimLogs(getDemoReagentClaimLogs());
      setClaimLoading(false);
      return;
    }
    setClaimLoading(true);
    try {
      const response = await getRecentActivities(200);
      if (response.success && Array.isArray(response.data)) {
        const logs = response.data.filter((log) => log.type === 'reagent' && log.action === 'use');
        setClaimLogs(logs);
      } else {
        setClaimLogs([]);
      }
    } catch {
      setClaimLogs([]);
    } finally {
      setClaimLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
    fetchProcurementApplications();
    fetchProcurementStatus();
    fetchClaimLogs();
  }, []);

  React.useEffect(() => {
    const handleApprovalUpdated = () => {
      fetchProcurementApplications();
      fetchProcurementStatus();
    };
    window.addEventListener('approvalStatusUpdated', handleApprovalUpdated);
    return () => window.removeEventListener('approvalStatusUpdated', handleApprovalUpdated);
  }, []);

  React.useEffect(() => {
    const handleStockUpdated = () => {
      fetchData();
      fetchClaimLogs();
      fetchProcurementApplications();
      fetchProcurementStatus();
    };
    window.addEventListener('reagentStockUpdated', handleStockUpdated);
    return () => window.removeEventListener('reagentStockUpdated', handleStockUpdated);
  }, []);

  const stats = useMemo(() => {
    const sku = data.length;
    const totalQty = data.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    const low = data.filter((r) => {
      const th = r.restockThreshold;
      if (th == null || !Number.isFinite(Number(th)) || Number(th) <= 0) return false;
      return (Number(r.quantity) || 0) <= Number(th);
    }).length;
    return { sku, totalQty, low };
  }, [data]);

  const procurementQueueCount = useMemo(
    () =>
      procurementApplications.filter((a) => {
        if (a.category !== '试剂申请' || a.status !== '已通过') return false;
        const status = procurementStatusMap[a.key] || '待采购';
        return status !== '已入库' && status !== '不补货';
      }).length,
    [procurementApplications, procurementStatusMap]
  );

  React.useEffect(() => {
    if (!canManageReagents && key === 'stock') {
      setKey('showcase');
    }
  }, [canManageReagents, key]);

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>试剂管理</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">试剂管理</h1>
          <p className="mm-subtitle">浏览试剂展示、维护库存与处理待采购申请，与实验室物资规范保持一致。</p>
        </div>
      </header>

      <section className="mm-stats mm-stats--quad" aria-label="试剂概览">
        <article className="mm-stat">
          <span className="mm-stat-label">登记品种</span>
          <span className="mm-stat-value">{stats.sku}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="science" sizePx={18} />
            <span>试剂条目</span>
          </div>
          <LimsNavIcon name="science" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">库存总量</span>
          <span className="mm-stat-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {stats.totalQty}
          </span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="analytics" sizePx={18} />
            <span>数量合计</span>
          </div>
          <LimsNavIcon name="bar_chart" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">低库存</span>
          <span className={`mm-stat-value${stats.low > 0 ? ' mm-stat-value--alert' : ''}`}>{stats.low}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="warning" sizePx={18} />
            <span>已设补货阈值且库存 ≤ 阈值的品种</span>
          </div>
          <LimsNavIcon name="notification_important" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">待采购队列</span>
          <span className="mm-stat-value">{procurementQueueCount}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="shopping_cart" sizePx={18} />
            <span>已通过审批待处理</span>
          </div>
          <LimsNavIcon name="local_shipping" sizePx={120} className="mm-stat-deco" />
        </article>
      </section>

      <Tabs activeKey={key} onSelect={(k) => setKey(k || 'showcase')} className="mb-2 border-0 lims-resource-tabs">
        <Tab eventKey="showcase" title="试剂展示">
          <ReagentShowcase
            data={data}
            loading={loading}
            onRefresh={fetchData}
            onClaimSubmitted={() => {
              fetchData();
              fetchClaimLogs();
            }}
          />
        </Tab>
        {canManageReagents && (
          <Tab eventKey="stock" title="库存管理">
            <ReagentStock
              data={data}
              setData={setData}
              loading={loading}
              onRefresh={fetchData}
              canManage={canManageReagents}
            />
          </Tab>
        )}
        <Tab eventKey="procurement" title="待采购">
          <div className="text-muted small mb-2">
            仅展示已完成全部审批（状态为「已通过」）的试剂申请。
            {!canManageReagents && ' 普通成员可查看本人申请进度，并在“已到货”后填写放置位置入库。'}
          </div>
          <ReagentProcurement
            applications={procurementApplications}
            canManage={canManageReagents}
            onRefresh={fetchProcurementApplications}
          />
        </Tab>
        <Tab eventKey="claims" title="申领记录">
          <div className="mm-table-shell mt-2">
            <div className="mm-table-toolbar">
              <div className="mm-toolbar-meta">共 {claimLogs.length} 条申领记录</div>
            </div>
            <div className="mm-sheet-scroll">
              {claimLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <p className="mt-2 text-muted mb-0">加载中...</p>
                </div>
              ) : claimLogs.length === 0 ? (
                <div className="mm-empty">暂无申领记录</div>
              ) : (
                <table className="mm-sheet">
                  <thead>
                    <tr>
                      <th>申领人</th>
                      <th>时间</th>
                      <th>记录</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claimLogs.map((log) => (
                      <tr key={log.id} className="mm-row">
                        <td>{log.actor?.name || '未知'}</td>
                        <td>{new Date(log.time).toLocaleString()}</td>
                        <td>{log.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default ReagentManagement;
